import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PrinterIcon } from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useRealtime } from "@/lib/useRealtime";
import {
  fetchHeroProjectSteps,
  getColumnSettings,
  getProjectTableHeroFields,
  listOrdner,
  listOrdnerForYearCustomer,
  listOrdersWithTotal,
  findSOrdnerByNummer,
  listSOrdner,
  peekSOrdnerList,
  saveColumnSettings,
} from "@/lib/projects";
import type { Order, ProjectColumnSettings, ProjectColumnView } from "@/lib/projects";
import { ApiError } from "@/lib/apiClient";
import { CollapsibleSection } from "@/components/ui/CollapsibleSection";
import { FilterBar } from "@/pages/projekte/FilterBar";
import { ProjectTable } from "@/pages/projekte/ProjectTable";
import { ProjectColumnsDialog } from "@/pages/projekte/ProjectColumnsDialog";
import { SearchInput } from "@/pages/projekte/SearchInput";
import {
  buildProjectFilterContextExtras,
  buildProjectFilterHeading,
  buildSOrdnerFilterHeading,
  usablePlaceLabel,
} from "@/pages/projekte/projectFilterSummary";
import {
  peekSOrdnerAddressTitle,
  resolveSOrdnerAddressTitle,
} from "@/pages/projekte/sordnerAddressTitle";
import { NewProjectButton } from "@/components/NewProjectButton";
import { NewOrderButton } from "@/components/NewOrderButton";
import { useOnProjectCreated } from "@/pages/projekte/useNewProject";
import {
  clampColumnWidth,
  getDefaultColumns,
  normalizeColumnSelection,
  getAvailableColumns,
} from "@/pages/projekte/columns";
import type { HeroCustomField } from "@/pages/projekte/columns";
import type { ProjectFilters } from "@/pages/projekte/useProjectFilters";
import { useProjectFilters, projectHasActiveFilters } from "@/pages/projekte/useProjectFilters";
import type { NewProjectOrdnerPrefill } from "@/pages/projekte/newProjectContextValue";
import { useHoursUsage } from "@/pages/projekte/useHoursUsage";
import { useBookedHours } from "@/pages/projekte/useBookedHours";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import { useSOrdnerOverview } from "@/sordnerOverview/useSOrdnerOverview";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import { isElectronWithOpenFolder } from "@/lib/electron";
import { TableSortResetBar, useTableSort } from "@/components/dataTable";
import { ProjectPrintPreviewDialog } from "@/pages/projekte/ProjectPrintPreviewDialog";

const PAGE_SIZE = 50;

type ProjectsFirstPageCacheEntry = {
  orders: Order[];
  totalCount: number | null;
};

// Stale-while-revalidate-Seed für die erste Seite: zeigt beim erneuten Öffnen der
// Projekte-Seite sofort die zuletzt geladenen Daten (kein Spinner) und lädt im
// Hintergrund frisch nach. Modul-Ebene, da die Seite beim Routenwechsel
// unmountet. Bewusst klein gehalten (nur die jeweils erste Seite pro Filter).
const _projectsFirstPageCache = new Map<string, ProjectsFirstPageCacheEntry>();

function formatProjectCountLabel(count: number): string {
  if (count === 1) return "· 1 Projekt";
  return `· ${count} Projekte`;
}

export function ProjekteIndexPage() {
  const { filters, update, reset } = useProjectFilters();
  const { sort, toggle, reset: resetSort } = useTableSort();
  const { user } = useCurrentUser();

  /** Spalte „Ordner öffnen“ wie in der klassischen App: nur Büro + Electron. */
  const showOpenFolderColumn = useMemo(
    () => userIsBuero(user) && isElectronWithOpenFolder(),
    [user],
  );
  const canViewSOrdnerOverview = useMemo(() => userIsBuero(user), [user]);

  const [orders, setOrders] = useState<Order[]>([]);
  /** Gesamtanzahl für die aktiven Filter (nicht nur die geladene Seite). */
  const [totalCount, setTotalCount] = useState<number | null>(null);
  // Wird bei jedem erfolgreichen Listen-Load erhöht, um die asynchrone
  // Hero-Step-Anreicherung erneut anzustoßen (auch nach Realtime-Reloads).
  const [ordersToken, setOrdersToken] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [heroFields, setHeroFields] = useState<HeroCustomField[]>([]);
  const [settings, setSettings] = useState<ProjectColumnSettings>({
    desktop: null,
    mobile: null,
  });
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const isMountedRef = useRef(true);
  // Laufende Nummer der Listen-Requests: Antworten, die zu einem älteren
  // Request gehören (z. B. langsame Suche nach "2-1", während schon "2-1#15"
  // gesucht wird), dürfen den aktuellen Zustand nicht mehr überschreiben.
  const requestSeqRef = useRef(0);
  // Bricht den vorherigen Listen-Fetch ab, sobald eine neue Suche/Filterung
  // startet – verhindert Request-Stapel und Race Conditions auf dem Server.
  const listAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      listAbortRef.current?.abort();
      listAbortRef.current = null;
    };
  }, []);

  // Hero-Felder + gespeicherte Settings parallel laden
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getProjectTableHeroFields().catch(() => []),
      getColumnSettings().catch(() => ({ desktop: null, mobile: null })),
    ]).then(([fields, columnSettings]) => {
      if (cancelled) return;
      setHeroFields(fields);
      setSettings(columnSettings);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Aktive Spaltenliste (responsiv: Desktop vs. Mobile)
  const isMobile = useMediaQuery("(max-width: 767px)");
  const visibleColumns = useMemo(() => {
    const view = isMobile ? settings.mobile : settings.desktop;
    const available = getAvailableColumns(heroFields);
    let cols: string[];
    if (view?.columns?.length) {
      cols = normalizeColumnSelection(view.columns, available);
    } else {
      cols = getDefaultColumns(heroFields);
    }
    if (!showOpenFolderColumn) {
      cols = cols.filter((c) => c !== "open");
    }
    return cols;
  }, [isMobile, settings, heroFields, showOpenFolderColumn]);

  // Aktive Spaltenbreiten (Desktop). Auf Mobil verzichten wir auf Resize.
  const visibleWidths = useMemo<Record<string, number>>(() => {
    const view = isMobile ? settings.mobile : settings.desktop;
    const widths = view?.widths ?? {};
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(widths)) {
      const num = Number(value);
      if (Number.isFinite(num) && num > 0) out[key] = clampColumnWidth(num);
    }
    return out;
  }, [isMobile, settings]);

  const needsTimesheets = visibleColumns.includes("timesheets");

  // Stabile Schlüssel für die Filter-Parameter
  const requestParams = useMemo(() => {
    return {
      q: filters.q || undefined,
      jahr: filters.jahr === null ? undefined : filters.jahr,
      kunde: filters.kunde || undefined,
      ordner: filters.ordner || undefined,
      sordner: filters.sordner || undefined,
      status: filters.status.length ? filters.status.join(",") : undefined,
      sort: sort?.key,
      direction: sort?.direction,
      // Timesheets nur mitladen, wenn die Spalte „NW-Nr.“ sichtbar ist –
      // sonst unnötig schwere json_agg-Subqueries pro Zeile.
      include_timesheets: needsTimesheets,
      include_timesheet_numbers: false,
      include_hero_steps: false,
    };
  }, [filters, sort, needsTimesheets]);

  // Lade-Funktion (initial / refetch)
  const loadFirstPage = useCallback(async () => {
    const requestId = ++requestSeqRef.current;
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    const cacheKey = JSON.stringify(requestParams);
    // SWR: vorhandene Daten sofort zeigen und still revalidieren – sonst Spinner.
    const seeded = _projectsFirstPageCache.get(cacheKey);
    if (seeded) {
      setOrders(seeded.orders);
      setTotalCount(seeded.totalCount);
      setHasMore(seeded.orders.length >= PAGE_SIZE);
      setLoading(false);
    } else {
      setLoading(true);
      setTotalCount(null);
    }
    setError(null);
    try {
      // DB-only laden (ohne Hero-Steps) – die schnelle, kleine Payload sorgt
      // dafür, dass der erste Render praktisch ohne Wartezeit erscheint.
      // Hero-Steps werden anschließend asynchron nachgeladen.
      const { orders: list, totalCount: total } = await listOrdersWithTotal(
        {
          ...requestParams,
          limit: PAGE_SIZE,
          offset: 0,
        },
        { signal: controller.signal },
      );
      if (!isMountedRef.current) return;
      // Cache immer füllen, aber den sichtbaren Zustand nur aktualisieren,
      // wenn dies noch der neueste Request ist (sonst überschreibt eine
      // langsame alte Suche die Ergebnisse der aktuellen Eingabe).
      _projectsFirstPageCache.set(cacheKey, { orders: list, totalCount: total });
      if (requestId !== requestSeqRef.current) return;
      setOrders(list);
      setTotalCount(total);
      setHasMore(list.length >= PAGE_SIZE);
      setOrdersToken((t) => t + 1);
    } catch (err) {
      if (
        (err instanceof DOMException || err instanceof Error) &&
        err.name === "AbortError"
      ) {
        return;
      }
      if (!isMountedRef.current) return;
      if (requestId !== requestSeqRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Projekte konnten nicht geladen werden.";
      setError(message);
      // Bei Fehler die (ggf. geseedeten) Daten stehen lassen, statt sie zu leeren.
      if (!seeded) {
        setOrders([]);
        setTotalCount(null);
        setHasMore(false);
      }
    } finally {
      if (isMountedRef.current && requestId === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [requestParams]);

  // Initialer Load + Refetch bei Filteränderung
  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    // Gehört zur aktuell sichtbaren ersten Seite; wenn zwischenzeitlich neu
    // gesucht/gefiltert wurde, darf das Ergebnis nicht mehr angehängt werden.
    const requestId = requestSeqRef.current;
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    setLoadingMore(true);
    try {
      const { orders: list } = await listOrdersWithTotal(
        {
          ...requestParams,
          limit: PAGE_SIZE,
          offset: orders.length,
        },
        { signal: controller.signal },
      );
      if (!isMountedRef.current) return;
      if (requestId !== requestSeqRef.current) return;
      setOrders((prev) => [...prev, ...list]);
      setHasMore(list.length >= PAGE_SIZE);
      setOrdersToken((t) => t + 1);
    } catch (err) {
      if (
        (err instanceof DOMException || err instanceof Error) &&
        err.name === "AbortError"
      ) {
        return;
      }
      console.warn("[Projekte] loadMore failed:", err);
    } finally {
      if (isMountedRef.current && requestId === requestSeqRef.current) {
        setLoadingMore(false);
      }
    }
  }, [loading, loadingMore, hasMore, orders.length, requestParams]);

  // Realtime: bei Änderungen einfach die erste Seite neu laden
  useRealtime({
    scopes: ["auftraege:list"],
    domains: ["auftraege", "projects", "hero"],
    events: [
      "auftraege_changed",
      "projects_changed",
      "hero_changed",
      "auftrag_neu",
      "hero_custom_fields_updated",
    ],
    onChange: () => {
      void loadFirstPage();
    },
  });

  // Liste neu laden, wenn irgendwo in der App ein Projekt angelegt wurde.
  useOnProjectCreated(() => {
    void loadFirstPage();
  });

  const { open: openProjectOverview } = useProjectOverview();
  const { open: openSOrdnerOverview } = useSOrdnerOverview();

  function handleRowOpen(row: Order) {
    openProjectOverview(row.id);
  }

  async function handleSaveColumnSettings(next: ProjectColumnSettings) {
    try {
      const saved = await saveColumnSettings(next);
      if (isMountedRef.current) setSettings(saved);
    } catch (err) {
      console.warn("[Projekte] saveColumnSettings failed:", err);
      // Lokal trotzdem übernehmen, damit der UI-Zustand passt.
      if (isMountedRef.current) setSettings(next);
    }
  }

  // Spaltenbreiten verwenden denselben Settings-Store. Wir aktualisieren
  // den lokalen Zustand sofort (für sofortiges Feedback) und debouncen den
  // Server-Call, damit beim Drag nicht jeder Pixel-Schritt geschickt wird.
  const widthSaveTimerRef = useRef<number | null>(null);
  const pendingSettingsRef = useRef<ProjectColumnSettings | null>(null);

  useEffect(() => {
    return () => {
      if (widthSaveTimerRef.current) {
        window.clearTimeout(widthSaveTimerRef.current);
      }
    };
  }, []);

  const handleColumnWidthChange = useCallback(
    (key: string, width: number) => {
      const clamped = clampColumnWidth(width);
      setSettings((prev) => {
        const view: "desktop" | "mobile" = isMobile ? "mobile" : "desktop";
        const current: ProjectColumnView = prev[view] ?? { columns: [], widths: {} };
        const nextView: ProjectColumnView = {
          columns: current.columns ?? [],
          widths: { ...(current.widths ?? {}), [key]: clamped },
        };
        const nextSettings: ProjectColumnSettings = {
          ...prev,
          [view]: nextView,
        };
        pendingSettingsRef.current = nextSettings;
        return nextSettings;
      });

      if (widthSaveTimerRef.current) {
        window.clearTimeout(widthSaveTimerRef.current);
      }
      widthSaveTimerRef.current = window.setTimeout(() => {
        const payload = pendingSettingsRef.current;
        pendingSettingsRef.current = null;
        widthSaveTimerRef.current = null;
        if (!payload) return;
        saveColumnSettings(payload).catch((err) => {
          console.warn("[Projekte] saveColumnWidths failed:", err);
        });
      }, 500);
    },
    [isMobile],
  );

  const ordnerHeadingLine = useOrdnerHeadingLine(filters);
  const sordnerHeadingInfo = useSOrdnerHeadingInfo(filters);
  const filterHeading = useMemo(
    () =>
      buildProjectFilterHeading(filters, {
        ordnerLine: ordnerHeadingLine,
        sordnerLine: sordnerHeadingInfo.line,
      }),
    [filters, ordnerHeadingLine, sordnerHeadingInfo.line],
  );
  const filterExtras = useMemo(
    () =>
      buildProjectFilterContextExtras(filters),
    [filters],
  );

  const newProjectOrdnerPrefill = useMemo(
    () => buildNewProjectOrdnerPrefill(filters),
    [filters.jahr, filters.ordner, filters.sordner, filters.kunde],
  );

  const showHoursUsage = visibleColumns.includes("hours_usage");
  const hoursUsageById = useHoursUsage(orders, showHoursUsage);
  const showBookedHours = visibleColumns.includes("booked_hours");
  const bookedHoursById = useBookedHours(orders, showBookedHours);

  // Hero-Status-Step + Kundenname-Fallback asynchron nachladen (nicht blockierend).
  const ordersRef = useRef(orders);
  ordersRef.current = orders;
  const needsHeroKundeFallback = useMemo(
    () =>
      orders.some(
        (o) =>
          o.use_hero_customer_name_in_projects &&
          !(o.hero_kunde && String(o.hero_kunde).trim()),
      ),
    [orders],
  );
  const needHeroEnrich = needsHeroKundeFallback;

  useEffect(() => {
    if (!needHeroEnrich) return;
    const list = ordersRef.current;
    const pids = Array.from(
      new Set(
        list
          .map((o) => o.hero_project_id)
          .filter((id): id is number => typeof id === "number" && id > 0),
      ),
    );
    if (!pids.length) return;

    let cancelled = false;
    void (async () => {
      const stepMap = await fetchHeroProjectSteps(pids);
      if (cancelled || !isMountedRef.current || stepMap.size === 0) return;
      setOrders((prev) =>
        prev.map((o) => {
          const pid = o.hero_project_id;
          if (typeof pid !== "number") return o;
          const info = stepMap.get(pid);
          if (!info) return o;
          const next: Order = {
            ...o,
            hero_step_id: info.hero_step_id ?? null,
            hero_step_name: info.hero_step_name ?? null,
          };
          if (
            o.use_hero_customer_name_in_projects &&
            !(o.hero_kunde && String(o.hero_kunde).trim()) &&
            info.hero_kunde
          ) {
            next.hero_kunde = info.hero_kunde;
          }
          return next;
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
    // ordersToken stößt nach jedem Load (auch Realtime-Reload) neu an; das Mergen
    // selbst erhöht den Token nicht und löst daher keine Schleife aus.
  }, [needHeroEnrich, ordersToken]);

  return (
    <AppShell title="Projekte">
      <>
      <div className="space-y-4 md:space-y-5">
        {/* Eine Kachel: Überschrift (wie klassisch) + Suche + Spalten + Projekt anlegen */}
        <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5 md:py-4">
          <div className="flex flex-col gap-3 md:gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-5">
            <div className="min-w-0 flex-1 space-y-1 lg:max-w-[min(100%,40rem)]">
              <h2 className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-lg font-semibold leading-snug tracking-tight text-label md:text-xl">
                <span className="min-w-0 break-words">{filterHeading}</span>
                {filters.jahr === "S" &&
                filters.sordner.trim() &&
                sordnerHeadingInfo.sordnerId &&
                canViewSOrdnerOverview ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                    onClick={() => openSOrdnerOverview(sordnerHeadingInfo.sordnerId!)}
                  >
                    S-Ordner öffnen
                  </Button>
                ) : null}
                <span
                  className="shrink-0 whitespace-nowrap text-sm font-medium tabular-nums text-label-secondary md:text-[15px]"
                  aria-live="polite"
                >
                  {loading && orders.length === 0
                    ? "· Lade …"
                    : formatProjectCountLabel(totalCount ?? orders.length)}
                </span>
              </h2>
              {filterExtras ? (
                <p className="text-xs text-label-secondary md:text-sm">{filterExtras}</p>
              ) : null}
            </div>
            <div className="flex min-w-0 shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 lg:max-w-full xl:min-w-[min(100%,36rem)] xl:flex-1 xl:justify-end">
              <SearchInput
                value={filters.q}
                onChange={(q) => update({ q })}
                debounceMs={300}
                placeholder="Projekte durchsuchen: Nummer, Angebot, Kunde, Ort …"
                className="w-full min-w-0 flex-1 sm:min-w-[12rem]"
              />
              <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  className="min-w-0 whitespace-nowrap"
                  leftIcon={<PrinterIcon className="h-4 w-4" />}
                  onClick={() => setPrintPreviewOpen(true)}
                >
                  Drucken
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className="min-w-0 whitespace-nowrap hidden md:inline-flex"
                  onClick={() => setColumnsDialogOpen(true)}
                >
                  Spalten
                </Button>
                <NewProjectButton
                  className="min-w-0 whitespace-nowrap"
                  ordnerPrefill={newProjectOrdnerPrefill}
                />
                <NewOrderButton variant="secondary" className="min-w-0 whitespace-nowrap" />
              </div>
            </div>
          </div>
        </section>

        <CollapsibleSection
          sectionKey="projekte.filter"
          title="Filter"
          summary={projectHasActiveFilters(filters) ? "Filter aktiv" : "Alle Projekte"}
          mobileAlwaysExpanded
          actions={
            projectHasActiveFilters(filters) ? (
              <button
                type="button"
                onClick={() => reset()}
                className="rounded-lg px-2 py-1 text-xs font-medium text-accent transition hover:bg-fill-tertiary"
              >
                Zurücksetzen
              </button>
            ) : null
          }
        >
          <FilterBar
            filters={filters}
            onChange={update}
            onReset={reset}
            onSOrdnerOpen={
              canViewSOrdnerOverview
                ? (s) => openSOrdnerOverview(s.id)
                : undefined
            }
          />
        </CollapsibleSection>

        {error && (
          <Alert variant="error" title="Liste konnte nicht geladen werden">
            {error}
          </Alert>
        )}

        {/* Tabelle */}
        <section>
          {loading && orders.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-label-secondary">
              <Spinner />
              <span className="ml-2 text-sm">Projekte werden geladen …</span>
            </div>
          ) : (
            <>
              <TableSortResetBar visible={sort != null} onReset={resetSort} />
              <ProjectTable
                rows={orders}
                columns={visibleColumns}
                heroFields={heroFields}
                widths={visibleWidths}
                highlightQuery={filters.q}
                hoursUsageById={hoursUsageById}
                bookedHoursById={bookedHoursById}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={hasMore}
                onLoadMore={loadMore}
                onRowOpen={handleRowOpen}
                onColumnWidthChange={handleColumnWidthChange}
                sort={sort}
                onSort={toggle}
                onHeroCreated={(orderId, heroProjectId) => {
                  setOrders((prev) =>
                    prev.map((o) =>
                      o.id === orderId
                        ? { ...o, hero_project_id: heroProjectId }
                        : o,
                    ),
                  );
                  setOrdersToken((t) => t + 1);
                }}
              />
              {!loading && orders.length === 0 && !error && projectHasActiveFilters(filters) ? (
                <div className="mt-4 flex flex-col items-center gap-3 rounded-ios bg-fill-quaternary/25 px-4 py-4 text-center shadow-ios ring-1 ring-separator/30 dark:ring-separator/45">
                  <p className="max-w-md text-sm text-label-secondary">
                    Es wurden keine Projekte gefunden. Alle Filter zurücksetzen, um die Chance auf
                    Treffer zu erhöhen.
                  </p>
                  <Button type="button" variant="secondary" onClick={() => reset()}>
                    Alle Filter auf „Alle“ setzen
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>

      <ProjectColumnsDialog
        open={columnsDialogOpen}
        onClose={() => setColumnsDialogOpen(false)}
        heroFields={heroFields}
        settings={settings}
        onSave={handleSaveColumnSettings}
        showOpenFolderColumn={showOpenFolderColumn}
      />
      <ProjectPrintPreviewDialog
        open={printPreviewOpen}
        onClose={() => setPrintPreviewOpen(false)}
        filters={filters}
        sort={sort}
        columns={visibleColumns}
        heroFields={heroFields}
        title={filterHeading}
        extras={filterExtras}
      />
      </>
    </AppShell>
  );
}

/* ─────────────────────────── Helper ─────────────────────────── */

/** Ordner aus aktuellem Listenfilter für „Projekt anlegen“ (s. auftraege.js). */
function buildNewProjectOrdnerPrefill(filters: ProjectFilters): NewProjectOrdnerPrefill | null {
  if (filters.jahr === "S") {
    const nummer = filters.sordner.trim();
    if (!nummer) return null;
    return { type: "s", sordnerNummer: nummer };
  }

  const nummer = filters.ordner.trim();
  if (!nummer) return null;

  // Vorjahres-Filter dürfen kein Projekt in diesem Jahr anlegen.
  // Die Ordnernummer wird nur im laufenden Jahr aufgelöst.
  return {
    type: "projekt",
    ordnerNummer: nummer,
    jahr: new Date().getFullYear(),
    kunde: filters.kunde.trim() || undefined,
  };
}

/** „Nummer – Bezeichnung“ für die Seitenüberschrift, passend zur aktuellen Filterkontext-Ordnerliste. */
function useOrdnerHeadingLine(filters: ProjectFilters): string | null {
  const [line, setLine] = useState<string | null>(null);

  useEffect(() => {
    const nummer = filters.ordner.trim();
    if (!nummer || filters.jahr === "S") {
      setLine(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        let list: Awaited<ReturnType<typeof listOrdner>> = [];
        if (typeof filters.jahr === "number") {
          list = filters.kunde
            ? await listOrdnerForYearCustomer(filters.kunde, filters.jahr)
            : await listOrdner(filters.jahr);
        } else if (filters.jahr === null) {
          const all = await listOrdner();
          list = filters.kunde
            ? all.filter((o) => o.kunden_name === filters.kunde)
            : all;
        } else {
          if (!cancelled) setLine(null);
          return;
        }

        const needle = nummer.toLowerCase();
        const hit = list.find(
          (o) => o.nummer.trim().toLowerCase() === needle || o.nummer === nummer,
        );
        if (cancelled) return;
        if (!hit) {
          setLine(nummer);
          return;
        }
        const desc = (hit.beschreibung || "").trim();
        setLine(desc ? `${hit.nummer.trim()} – ${desc}` : hit.nummer.trim());
      } catch {
        if (!cancelled) setLine(nummer);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filters.ordner, filters.jahr, filters.kunde]);

  return line;
}

type SOrdnerHeadingInfo = {
  line: string | null;
  sordnerId: number | null;
};

/** „Nummer – Ort · Ort-Zusatz – Bezeichnung“ für die Seitenüberschrift bei S-Ordner-Filtern. */
function useSOrdnerHeadingInfo(filters: ProjectFilters): SOrdnerHeadingInfo {
  const nummer = filters.jahr === "S" ? filters.sordner.trim() : "";
  const [listRevision, setListRevision] = useState(0);
  const [addressTitles, setAddressTitles] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (filters.jahr !== "S") return;
    let cancelled = false;
    void listSOrdner()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setListRevision((n) => n + 1);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.jahr]);

  const hit = useMemo(() => {
    if (!nummer) return null;
    const list = peekSOrdnerList();
    return list ? findSOrdnerByNummer(list, nummer) ?? null : null;
  }, [nummer, listRevision]);

  const addressKey =
    hit?.hero_customer_id != null && hit.hero_address_id != null
      ? `${hit.hero_customer_id}:${hit.hero_address_id}`
      : "";
  const needsAddressLookup = Boolean(hit && addressKey && !usablePlaceLabel(hit.ort_name));

  useEffect(() => {
    if (!hit || !needsAddressLookup || !addressKey) return;
    const known = peekSOrdnerAddressTitle(hit.hero_customer_id, hit.hero_address_id);
    if (known !== undefined) {
      setAddressTitles((prev) => (prev[addressKey] === known ? prev : { ...prev, [addressKey]: known }));
      return;
    }
    void resolveSOrdnerAddressTitle(hit).then((label) => {
      setAddressTitles((prev) =>
        prev[addressKey] === label ? prev : { ...prev, [addressKey]: label },
      );
    });
  }, [hit, needsAddressLookup, addressKey]);

  return useMemo(() => {
    if (!nummer) return { line: null, sordnerId: null };
    if (!hit) return { line: null, sordnerId: null };
    const resolved = addressKey ? addressTitles[addressKey] : null;
    return {
      line: buildSOrdnerFilterHeading({
        ...hit,
        hero_address_title: resolved ?? null,
      }),
      sordnerId: hit.id,
    };
  }, [nummer, hit, addressKey, addressTitles]);
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}
